import axios, { AxiosRequestConfig } from 'axios';
import { UserStoreObject } from 'src/stores/UserStore';
import { AuthStoreObject } from 'src/stores/AuthStore';
import { clearMfaToken, getMfaToken } from 'src/utils/mfaToken';

let emrServicesBaseUrl = 'http://localhost:3001/api/';
if (import.meta.env.VITE_APP_ENV === 'dev') {
    emrServicesBaseUrl = 'https://emr-services-dev.dev.graphiumemr.com/api/';
} else if (import.meta.env.VITE_APP_ENV === 'pro') {
    emrServicesBaseUrl = 'https://emr-services-pro.graphiumemr.com/api/';
}

const DEFAULT_HEADERS = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'App-Name': import.meta.env.VITE_APP_NAME,
    'App-Version': import.meta.env.PACKAGE_VERSION,
    'App-Origin': window.location.host,
};
type ParamObject = { [key: string]: any };

function replaceUrlParam(url: string, pathParams?: ParamObject): string {
    if (url.includes('{') && url.includes('}') && pathParams) {
        let tempUrl: string = url;

        for (const [key, value] of Object.entries(pathParams)) {
            // Match the route/{routeId}
            let param: string = (tempUrl.match(/\{[A-Za-z0-9]*\}/) || [])[0];
            // Remove the brackets from the matching result
            if (param) {
                param = param.replace(/[\{\}]/g, '');
                if (typeof value !== 'undefined') {
                    tempUrl = tempUrl.replace(`{${param}}`, value);
                }
            }
        }

        return tempUrl;
    }
    return url;
}

function buildParams(options: RequestObj) {
    if (options.anonymous) {
        return options.params;
    }

    const params = Object.assign({}, options.params, {
        un: UserStoreObject.usrNm,
    });
    if (UserStoreObject.selectedOrg?.orgNmIntrnl) {
        params.org = UserStoreObject.selectedOrg.orgNmIntrnl;
    }
    return params;
}

function getCurrentOrgScope() {
    return UserStoreObject.selectedOrg?.orgId || UserStoreObject.selectedOrg?.orgNmIntrnl;
}

function buildHeaders(options: RequestObj): Record<string, string> {
    const mfaToken = getMfaToken(getCurrentOrgScope());
    const authorizationHeaders = {
        Authorization: 'bearer ' + AuthStoreObject.getAccessToken(),
        ...(mfaToken ? { 'X-Graphium-Mfa-Token': mfaToken } : {}),
    };
    const headers = !options.anonymous
        ? { ...DEFAULT_HEADERS, ...authorizationHeaders }
        : !options.external
        ? { ...DEFAULT_HEADERS }
        : {};
    return Object.entries(options.headers ? { ...headers, ...options.headers } : headers).reduce(
        (result, [key, value]) => ({ ...result, [key]: `${value ?? ''}` }),
        {},
    );
}

function buildFetchUrl(options: RequestObj): string {
    const path = replaceUrlParam(options.url, options.pathParams);
    const base = emrServicesBaseUrl.replace(/\/$/, '');
    const url = new URL(`${base}${path.startsWith('/') ? path : `/${path}`}`);
    const params = buildParams(options) || {};
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            url.searchParams.set(key, `${value}`);
        }
    });
    return url.toString();
}

export interface RequestObj {
    method: AxiosRequestConfig['method'];
    url: AxiosRequestConfig['url'];
    params?: ParamObject;
    pathParams?: ParamObject;
    data?: AxiosRequestConfig['data'];
    headers?: AxiosRequestConfig['headers'];
    anonymous?: boolean;
    external?: boolean;
    responseType?: AxiosRequestConfig['responseType'];
    withCredentials?: AxiosRequestConfig['withCredentials'];
}

export async function streamNdjson<T>(
    options: RequestObj,
    onEvent: (event: T) => void,
    signal?: AbortSignal,
): Promise<void> {
    const response = await fetch(buildFetchUrl(options), {
        method: options.method?.toUpperCase(),
        headers: buildHeaders(options),
        body: options.method !== 'get' ? JSON.stringify(options.data || {}) : undefined,
        signal,
    });
    if (!response.ok) {
        const message = (await response.text()).trim();
        throw new Error(message || `Request failed with HTTP ${response.status}`);
    }
    if (!response.body) {
        throw new Error('The test response could not be streamed.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let completed = false;
    try {
        while (true) {
            const { done, value } = await reader.read();
            buffer += decoder.decode(value, { stream: !done });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            lines.forEach((line) => {
                if (line.trim()) onEvent(JSON.parse(line) as T);
            });
            if (done) {
                completed = true;
                break;
            }
        }
        if (buffer.trim()) onEvent(JSON.parse(buffer) as T);
    } finally {
        if (!completed) await reader.cancel().catch(() => undefined);
    }
}

export default function request(options: RequestObj) {
    const url = replaceUrlParam(options.url, options.pathParams);
    const orgScope = getCurrentOrgScope();
    const newObj: AxiosRequestConfig = {
        headers: buildHeaders(options),
        params: buildParams(options),
        method: options.method,
        url,
        baseURL: emrServicesBaseUrl,
        withCredentials: options.withCredentials,
    };
    if (options.method !== 'get') {
        newObj.data = options.data;
    }
    if (options.responseType) {
        newObj.responseType = options.responseType;
    }

    return axios(newObj).catch((error) => {
        if (error?.response?.data?.code === 'MFA_REQUIRED') {
            clearMfaToken(orgScope);
        }
        throw error;
    });
}
